import { FC } from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, Seo } from "@shared/components";
import { CONTACTS, PRICING_FACTS } from "@shared/config/seo-schemas";
import { buildBotUrl } from "@shared/helper_lib/bot-link";
import { Mail, MessageCircle, Send } from "lucide-react";

/**
 * Контакты и сведения об исполнителе. Раньше реквизиты были только внутри оферты —
 * Яндекс для коммерческих сайтов отдельно оценивает, насколько легко их найти.
 */
export const ContactsPage: FC = () => {
  return (
    <>
      <Seo
        title="Контакты и реквизиты | Polotno"
        description="Контакты сервиса расшифровки аудио и видео Polotno: email поддержки, Telegram-бот, реквизиты исполнителя."
        path="/contacts"
      />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: "Контакты" }]} />

      <div className="container mx-auto px-4 py-12 md:px-6 lg:px-8">
        <h1 className="mb-4 text-center text-4xl font-bold">Контакты</h1>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          Пишите по любым вопросам о работе сервиса, тарифах и оплате — отвечаем на все обращения.
        </p>

        <div className="mx-auto max-w-3xl space-y-10">
          <section>
            <h2 className="mb-4 text-2xl font-semibold">Как с нами связаться</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <a
                href={`mailto:${CONTACTS.email}`}
                className="flex items-start gap-3 rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>
                  <span className="block font-semibold text-foreground">Электронная почта</span>
                  <span className="block text-sm text-muted-foreground">{CONTACTS.email}</span>
                </span>
              </a>

              <a
                href={CONTACTS.supportBotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>
                  <span className="block font-semibold text-foreground">Поддержка в Telegram</span>
                  <span className="block text-sm text-muted-foreground">Быстрее всего — здесь</span>
                </span>
              </a>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">О сервисе</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                «Полотно» — сервис автоматической расшифровки аудио и видео в текст. Он принимает файлы любых
                распространённых форматов и ссылки на открытые публикации в YouTube, VK Video и Rutube, распознаёт речь
                и возвращает текст с пунктуацией, таймкодами и разделением по спикерам.
              </p>
              <p>
                Работать можно через сайт или через{" "}
                <a
                  href={buildBotUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Telegram-бот
                </a>
                . Ограничения одинаковы везде: до {PRICING_FACTS.maxFileSizeGb} ГБ и до четырёх часов на один файл.
                Готовый результат выгружается в TXT, DOCX, PDF или SRT, а к длинным записям доступна AI-сводка с
                ключевыми моментами.
              </p>
              <p>
                Начать можно с пробного периода: {PRICING_FACTS.trial.minutes} минут за {PRICING_FACTS.trial.price} ₽ на{" "}
                {PRICING_FACTS.trial.days} дней. Все тарифы описаны на странице{" "}
                <Link to="/pricing" className="text-primary hover:underline">
                  тарифов
                </Link>
                .
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Реквизиты</h2>
            <div className="rounded-lg bg-muted/50 p-5">
              <dl className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-x-2">
                  <dt className="font-semibold text-foreground">Самозанятый:</dt>
                  <dd className="text-muted-foreground">{CONTACTS.legalName}</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="font-semibold text-foreground">ИНН:</dt>
                  <dd className="text-muted-foreground">{CONTACTS.taxId}</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="font-semibold text-foreground">Email:</dt>
                  <dd>
                    <a href={`mailto:${CONTACTS.email}`} className="text-primary hover:underline">
                      {CONTACTS.email}
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Условия оказания услуг описаны в{" "}
              <Link to="/offer" className="text-primary hover:underline">
                публичной оферте
              </Link>
              , обработка персональных данных — в{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                политике конфиденциальности
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Оплата</h2>
            <p className="text-muted-foreground">
              Оплата принимается банковскими картами через платёжные сервисы Lava и ЮKassa. Подписки продлеваются
              автоматически, отменить продление можно в личном кабинете в любой момент. Чек приходит на почту, указанную
              при оплате.
            </p>
          </section>

          <div className="rounded-lg border border-border p-6 text-center">
            <Send className="mx-auto mb-3 h-6 w-6 text-primary" />
            <p className="mb-4 text-muted-foreground">Не нашли ответ на свой вопрос?</p>
            <a
              href={CONTACTS.supportBotUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              Написать в поддержку
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
